import React, { useMemo } from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { EmptyState, Screen, SegmentedTabBar } from '../../design/components';

import { PurchaseOrderListView } from '../purchaseOrder';
import StockDeliveryListScreen from './StockDeliveryListScreen';

const Tab = createMaterialTopTabNavigator();

const StockDeliveryListView = () => {
	const styles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();

	const canAccessStockDeliveryList = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_LIST_PAGE));
	}, [hasPermission]);

	const canAccessPurchaseOrderList = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_LIST_PAGE));
	}, [hasPermission]);

	const canAccessAtLeastOneTab = canAccessStockDeliveryList || canAccessPurchaseOrderList;

	if (!canAccessAtLeastOneTab) {
		return (
			<Screen backLabel="Warehouse" heading="Stock deliveries">
				<EmptyState icon="lock" title="You do not have access to stock deliveries or purchase orders." />
			</Screen>
		);
	}

	return (
		<Screen
			backLabel="Warehouse"
			heading="Stock deliveries"
			subheading="Manage purchase orders and stock deliveries"
			scroll={false}
		>
			<Tab.Navigator
				initialRouteName={canAccessPurchaseOrderList ? 'PurchaseOrdersTab' : 'StockDeliveriesTab'}
				tabBar={props => <SegmentedTabBar {...props} />}
				screenOptions={{
					sceneStyle: styles.scene,
					lazy: true,
				}}
			>
				<Tab.Screen
					name="PurchaseOrdersTab"
					options={{ tabBarLabel: 'Purchase orders' }}
				>
					{() => <PurchaseOrderListView hideHeader={true} />}
				</Tab.Screen>
				<Tab.Screen
					name="StockDeliveriesTab"
					options={{ tabBarLabel: 'Stock deliveries' }}
				>
					{() => <StockDeliveryListScreen hideHeader={true} />}
				</Tab.Screen>
			</Tab.Navigator>
		</Screen>
	);
};

const createStyles = ({ ui: { colors } }) => ({
	scene: {
		backgroundColor: colors.canvas,
	},
});

export default StockDeliveryListView;
